import React, { useEffect, useState } from "react";
import "./ThirdPartyAuthScopes.styles.scss";

import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../redux/store";

import { addNewThirdPartyAuthDataThunkAction } from "../../../redux/thirdPArtyAuth/thirdPartyAuth.slice";
import { useSelector } from "react-redux";
import { createdTokenDataSelector , creationSuccessfulSelector} from "../../../redux/thirdPArtyAuth/thirdPartyAuth.selectors";

import { SCOPES } from "../variables/constants";
import { useSearchParams } from "react-router-dom";
import { Modal } from "@careexpand/cex-core-components/esm";
import axios from "axios";

interface ThirdPartyAuthScopesProps {
    isPatient: boolean;
    onScopesChange: (scopes: string[]) => void;
    tableSelectedPatients: number[];
}

interface GranularScopeOption {
    scope: string;
    display: string;
    description: string;
}

interface GranularOption {
    resourceType: string;
    resourceScope: string;
    granularScopes: GranularScopeOption[];
}

interface ScopeOptions {
    requiredScopes: string[];
    granularOptions: GranularOption[];
}

const ThirdPartyAuthScopes: React.FC<ThirdPartyAuthScopesProps> = ({
    isPatient,
    onScopesChange,
    tableSelectedPatients,
}) => {
    const [searchParams] = useSearchParams();
    const redirect_uri = searchParams.get("redirect_uri");
    const client_id = searchParams.get("client_id");
    const state = searchParams.get("state");
    const scope = searchParams.get("scope");
    const code_challenge = searchParams.get("code_challenge");
    const code_challenge_method = searchParams.get("code_challenge_method");
    const createdTokenData = useSelector(createdTokenDataSelector);
    const creationSuccessful = useSelector(creationSuccessfulSelector);
    const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
    const [selectedGranularScopes, setSelectedGranularScopes] = useState<Set<string>>(new Set());
    const [offlineAccessModalVisible, setOfflineAccessModalVisible] = useState(false);
    const [scopeOptions, setScopeOptions] = useState<ScopeOptions | null>(null);
    const [isLoadingScopeOptions, setIsLoadingScopeOptions] = useState(false);
    const dispatch = useDispatch<AppDispatch>();

    const allScopes = scope ? scope.split(" ") : isPatient
        ? [...SCOPES.OPENID, ...SCOPES.PATIENT]
        : [...SCOPES.OPENID, ...SCOPES.USER];
    
    // Fetch granular scope options from backend
    useEffect(() => {
        const fetchScopeOptions = async () => {
            if (!client_id || !scope) return;

            try {
                setIsLoadingScopeOptions(true);
                // eslint-disable-next-line no-console
                console.log("🔍 Fetching scope options from backend...");
                
                const response = await axios.get(
                    `${process.env.REACT_APP_SMART_API_URL || 'https://auth-api.dev.careexpandcloud.com'}/v1/cex/authrs/auth/scope-options`,
                    {
                        params: {
                            client_id,
                            scope
                        }
                    }
                );

                if (response.data.success) {
                    // eslint-disable-next-line no-console
                    console.log("✅ Scope options received:", response.data.data);
                    setScopeOptions(response.data.data);
                    
                    // Initialize selected scopes with required scopes
                    setSelectedScopes(response.data.data.requiredScopes);
                } else {
                    // eslint-disable-next-line no-console
                    console.error("❌ Failed to fetch scope options:", response.data.error);
                    // Fallback to original behavior
                    setScopeOptions(null);
                    const availableScopes = scope.split(" ");
                    setSelectedScopes(availableScopes);
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error("❌ Error fetching scope options:", error);
                // Fallback to original behavior
                setScopeOptions(null);
                const availableScopes = scope.split(" ");
                setSelectedScopes(availableScopes);
            } finally {
                setIsLoadingScopeOptions(false);
            }
        };

        fetchScopeOptions();
    }, [client_id, scope]);

    // Original initialization for non-granular scopes
    useEffect(() => {
        if (!scopeOptions) {
            const availableScopes = scope? scope.split(" "):isPatient
            ? [...SCOPES.OPENID, ...SCOPES.PATIENT]
            : [...SCOPES.OPENID, ...SCOPES.USER];
            setSelectedScopes(availableScopes);
        }
    }, [isPatient, scope, scopeOptions]);

    useEffect(() => {
        // Combine required scopes with selected granular scopes
        const finalScopes = scopeOptions 
            ? [...scopeOptions.requiredScopes, ...Array.from(selectedGranularScopes)]
            : selectedScopes;
        onScopesChange(finalScopes);
    }, [selectedScopes, selectedGranularScopes, onScopesChange, scopeOptions]);

    const handleScopeChange = (scopeToToggle: string) => {
        setSelectedScopes(prevScopes => {
            if (prevScopes.includes(scopeToToggle)) {
                return prevScopes.filter(s => s !== scopeToToggle);
            } else {
                return [...prevScopes, scopeToToggle];
            }
        });
    };

    // Check if a scope has granular options
    const hasGranularOptions = (scopeToCheck: string): GranularOption | null => {
        if (!scopeOptions) return null;
        return scopeOptions.granularOptions.find(opt => opt.resourceScope === scopeToCheck) || null;
    };

    // Handle parent scope change (e.g., patient/Condition.rs)
    // When parent is checked: ONLY send parent scope (do NOT send children)
    const handleParentScopeChange = (parentScope: string, granularOption: GranularOption) => {
        const isCurrentlySelected = selectedScopes.includes(parentScope);
        
        if (isCurrentlySelected) {
            // Uncheck parent - remove it from selected scopes
            setSelectedScopes(prev => prev.filter(s => s !== parentScope));
            // Also uncheck all children
            setSelectedGranularScopes(prev => {
                const newSet = new Set(prev);
                granularOption.granularScopes.forEach(gs => newSet.delete(gs.scope));
                return newSet;
            });
        } else {
            // Check parent - add it to selected scopes
            setSelectedScopes(prev => [...prev, parentScope]);
            // DO NOT automatically check children - let user select them individually
        }
    };

    // Handle granular scope change
    // When a child scope is selected, remove the parent scope (send only granular scopes)
    const handleGranularScopeChange = (granularScope: string) => {
        // Find which parent this granular scope belongs to
        const parentOption = scopeOptions?.granularOptions.find(opt => 
            opt.granularScopes.some(gs => gs.scope === granularScope)
        );
        
        setSelectedGranularScopes(prev => {
            const newSet = new Set(prev);
            if (newSet.has(granularScope)) {
                newSet.delete(granularScope);
            } else {
                newSet.add(granularScope);
                // Remove parent scope if a child is selected
                if (parentOption) {
                    setSelectedScopes(prevScopes => prevScopes.filter(s => s !== parentOption.resourceScope));
                }
            }
            return newSet;
        });
    };

    useEffect(() => {
        if(creationSuccessful &&createdTokenData&& createdTokenData.redirect_url) {
            window.location.href = createdTokenData.redirect_url;
        }   
    }, [creationSuccessful]);
    
    const checkAuthorize = () => {
        const finalScopes = scopeOptions 
            ? [...selectedScopes, ...Array.from(selectedGranularScopes)]
            : selectedScopes;
            
        if (isPatient && finalScopes.includes("offline_access")) {
            setOfflineAccessModalVisible(true);
        } else {
            handleAuthorize()
        }
    }
    
    const handleAuthorize = () => {
        try {
            setOfflineAccessModalVisible(false);
            (async () => {
                const finalScopes = scopeOptions 
                    ? [...selectedScopes, ...Array.from(selectedGranularScopes)]
                    : selectedScopes;
                    
                //eslint-disable-next-line
                console.log("=== FRONTEND AUTHORIZATION DEBUG ===");
                //eslint-disable-next-line
                console.log("PKCE Parameters:", { code_challenge, code_challenge_method });
                //eslint-disable-next-line
                console.log("Selected Scopes:", finalScopes);
                //eslint-disable-next-line
                console.log("All URL Params:", Object.fromEntries(searchParams.entries()));
                
                const data = {
                    scopes: finalScopes,
                    patients: tableSelectedPatients,
                    redirect_uri,
                    client_id,
                    state,
                    response_type: "code",
                    ...(code_challenge && { code_challenge }),
                    ...(code_challenge_method && { code_challenge_method })
                    }
                //eslint-disable-next-line
                console.log("Authorization data being sent:", data);
                //eslint-disable-next-line
                console.log("=====================================");
                dispatch(
                    addNewThirdPartyAuthDataThunkAction({ data })).then((response) => {
                        if(response.payload.redirect_url) {
                            window.location.href = response.payload.redirect_url;
                        }   
                    })
                    
                
            })();
        } catch (error) {
            //eslint-disable-next-line
            console.error(error);
        }
    };

    if (isLoadingScopeOptions) {
        return (
            <div className="third-party-auth-scopes">
                <h3 className="third-party-auth-scopes__header">Loading scope options...</h3>
            </div>
        );
    }

    return (
        
        <div className="third-party-auth-scopes">
            
            {offlineAccessModalVisible && <Modal closeModal={() => { setOfflineAccessModalVisible(false) }}
                children={<></> }
                title="Offline Access Requested"
                titleDescription="This application is requesting offline access. This means that it will be able to access your data even when you are not using the app."
                backButton={false}
                leftButtons={false}
                buttons={[{
                    onClick: () => { setOfflineAccessModalVisible(false) },
                    displayText: "Cancel",
                    theme: { primaryColor: "#FFF", textColor: "#FF4A4A" }
                },{
                    onClick: handleAuthorize,
                    displayText: "Continue",
                    theme: { primaryColor: "#FFF", textColor: "#009EFC" }
                }, ]} />
            }
            <h3 className="third-party-auth-scopes__header">Scopes</h3>
            
            <ul className="third-party-auth-scopes__list">
                {scopeOptions ? (
                    /* Show scopes with granular options if available */
                    <>
                        {/* Required scopes (non-granular) */}
                        {scopeOptions.requiredScopes.map((scopeItem, index) => {
                            const granularOption = hasGranularOptions(scopeItem);
                            
                            if (granularOption) {
                                // This scope has granular options - show parent with children
                                const isParentChecked = selectedScopes.includes(scopeItem);
                                
                                return (
                                    <li key={index} className="third-party-auth-scopes__item">
                                        <label className="third-party-auth-scopes__label">
                                            <input
                                                type="checkbox"
                                                className="third-party-auth-scopes__checkbox"
                                                checked={isParentChecked}
                                                onChange={() => handleParentScopeChange(scopeItem, granularOption)}
                                            />
                                            <span className="third-party-auth-scopes__scope-text">{scopeItem}</span>
                                        </label>
                                        
                                        {/* Nested granular scopes */}
                                        <ul className="third-party-auth-scopes__nested-list">
                                            {granularOption.granularScopes.map((granular, gIndex) => (
                                                <li key={gIndex} className="third-party-auth-scopes__nested-item">
                                                    <label className="third-party-auth-scopes__label third-party-auth-scopes__label--nested">
                                                        <input
                                                            type="checkbox"
                                                            className="third-party-auth-scopes__checkbox"
                                                            checked={selectedGranularScopes.has(granular.scope)}
                                                            onChange={() => handleGranularScopeChange(granular.scope)}
                                                        />
                                                        <span className="third-party-auth-scopes__scope-text">
                                                            {granular.display}
                                                            <span className="third-party-auth-scopes__scope-description">
                                                                {granular.description}
                                                            </span>
                                                        </span>
                                                    </label>
                                                </li>
                                            ))}
                                        </ul>
                                    </li>
                                );
                            } else {
                                // Regular scope without granular options - make it toggleable
                                return (
                                    <li key={index} className="third-party-auth-scopes__item">
                                        <label className="third-party-auth-scopes__label">
                                            <input
                                                type="checkbox"
                                                className="third-party-auth-scopes__checkbox"
                                                checked={selectedScopes.includes(scopeItem)}
                                                onChange={() => handleScopeChange(scopeItem)}
                                            />
                                            <span className="third-party-auth-scopes__scope-text">{scopeItem}</span>
                                        </label>
                                    </li>
                                );
                            }
                        })}
                        
                        {/* Granular options that aren't in required scopes */}
                        {scopeOptions.granularOptions
                            .filter(opt => !scopeOptions.requiredScopes.includes(opt.resourceScope))
                            .map((granularOption, index) => {
                                const isParentChecked = selectedScopes.includes(granularOption.resourceScope);
                                
                                return (
                                    <li key={`granular-${index}`} className="third-party-auth-scopes__item">
                                        <label className="third-party-auth-scopes__label">
                                            <input
                                                type="checkbox"
                                                className="third-party-auth-scopes__checkbox"
                                                checked={isParentChecked}
                                                onChange={() => handleParentScopeChange(granularOption.resourceScope, granularOption)}
                                            />
                                            <span className="third-party-auth-scopes__scope-text">{granularOption.resourceScope}</span>
                                        </label>
                                        
                                        {/* Nested granular scopes */}
                                        <ul className="third-party-auth-scopes__nested-list">
                                            {granularOption.granularScopes.map((granular, gIndex) => (
                                                <li key={gIndex} className="third-party-auth-scopes__nested-item">
                                                    <label className="third-party-auth-scopes__label third-party-auth-scopes__label--nested">
                                                        <input
                                                            type="checkbox"
                                                            className="third-party-auth-scopes__checkbox"
                                                            checked={selectedGranularScopes.has(granular.scope)}
                                                            onChange={() => handleGranularScopeChange(granular.scope)}
                                                        />
                                                        <span className="third-party-auth-scopes__scope-text">
                                                            {granular.display}
                                                            <span className="third-party-auth-scopes__scope-description">
                                                                {granular.description}
                                                            </span>
                                                        </span>
                                                    </label>
                                                </li>
                                            ))}
                                        </ul>
                                    </li>
                                );
                            })}
                    </>
                ) : (
                    /* Fallback to original simple scope list */
                    allScopes.map((scopeItem, index) => (
                        <li key={index} className="third-party-auth-scopes__item">
                            <label className="third-party-auth-scopes__label">
                                <input
                                    type="checkbox"
                                    className="third-party-auth-scopes__checkbox"
                                    checked={selectedScopes.includes(scopeItem)}
                                    onChange={() => handleScopeChange(scopeItem)}
                                />
                                <span className="third-party-auth-scopes__scope-text">{scopeItem}</span>
                            </label>
                        </li>
                    ))
                )}
            </ul>
            
            <button className="third-party-auth-scopes__button" onClick={()=>{ checkAuthorize()} }>
                Authorize
            </button>
        </div>
    );
};

export default ThirdPartyAuthScopes;
